/**
 * Quick View Store - Global Alpine.js Store
 * Handles product quick view modal functionality
 *
 * Usage:
 * - Alpine.store('quickView').open(productId)
 * - Alpine.store('quickView').close()
 */

export default {
  // State
  isOpen: false,
  loading: false,
  productId: null,
  productHtml: '',
  error: null,

  /**
   * Initialize store
   */
  init() {
    // No global event listeners - handled by x-on in template
  },

  /**
   * Open quick view modal for a product
   * @param {number} productId - Product ID to display
   * @param {string} productLink - Product URL (from $link variable)
   */
  async open(productId, productLink) {
    if (!productId) return;

    // Reset state - don't open modal yet
    this.loading = true;
    this.productId = productId;
    this.productHtml = '';
    this.error = null;

    // Get loading message from data attribute
    const loadingMsg = document.documentElement.dataset.loadingDefault || 'Loading...';

    // Show global loader
    if (window.Alpine && window.Alpine.store('loader')) {
      window.Alpine.store('loader').show('quick-view', loadingMsg);
    }

    try {
      // Use product link - server's X-Requested-With header automatically
      // triggers product.ajax.inc.php template instead of product.inc.php
      const url = productLink;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Set product HTML and open modal
      this.productHtml = html;
      this.loading = false;
      this.isOpen = true;

      // Hide global loader
      if (window.Alpine && window.Alpine.store('loader')) {
        window.Alpine.store('loader').hide('quick-view');
      }

      // NOTE: No manual Alpine.initTree() needed here.
      // Alpine v3's x-html directive automatically calls initTree()
      // after setting innerHTML. A second initTree() would cause
      // double initialization — resetting sliders, variants, etc.

    } catch (error) {
      this.error = window.tx('errorQuickView', 'Failed to load product details. Please try again.');
      this.loading = false;
      this.isOpen = true; // Open modal to show error state

      // Hide global loader
      if (window.Alpine && window.Alpine.store('loader')) {
        window.Alpine.store('loader').hide('quick-view');
      }
    }
  },

  /**
   * Close quick view modal
   */
  close() {
    // CRITICAL: Destroy Alpine components before clearing HTML
    const modalContent = document.querySelector('[x-ref="quickViewContent"]');
    if (modalContent) {
      // Use Alpine's official API - calls destroy() lifecycle on all components
      window.Alpine.destroyTree(modalContent);
      modalContent.innerHTML = '';
    }

    // Reset state
    this.isOpen = false;
    this.loading = false;
    this.productId = null;
    this.productHtml = '';
    this.error = null;
  }
};
